import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { GlobalService } from '../../services/global.service';
import { SessionService } from '../../services/session.service';
import { CaptureService } from '../../services/capture.service';
import { AccessMode } from '../constants/app.constants';

export function redirectToActiveSession(router: Router, session: SessionService): void {
  const current = session.currentSession();
  if (session.getAccessMode() === AccessMode.Session && current?.tireSpecSessionGuid) {
    router.navigate(['/session', current.tireSpecSessionGuid]);
  } else if (session.getAccessMode() === AccessMode.WebsiteGuid && current?.websiteGuid) {
    router.navigate(['/', current.websiteGuid]);
  } else {
    router.navigate(['/not-found'], { state: { errorMessage: 'No session information provided. Please access the application through a valid link or QR code.' } });
  }
}

export const validGuidGuard: CanActivateFn = (route) => {
  const router = inject(Router);
  const globalService = inject(GlobalService);

  const guid = route.paramMap.get('guid') ?? route.paramMap.get('websiteGuid');
  if (globalService.isValidGUID(guid)) {
    return true;
  }

  router.navigate(['/not-found'], { state: { errorMessage: route.data['invalidGuidMessage'] } });
  return false;
};

export const hasCapturedImageGuard: CanActivateFn = () => {
  const router = inject(Router);
  const session = inject(SessionService);
  const captureService = inject(CaptureService);

  if (captureService.getImage()) {
    return true;
  }

  redirectToActiveSession(router, session);
  return false;
};

export const hasSidewallInputGuard: CanActivateFn = () => {
  const router = inject(Router);
  const session = inject(SessionService);
  const captureService = inject(CaptureService);

  if (captureService.getOcrResult() || captureService.isManualEntry() || captureService.getSavedFormState()) {
    return true;
  }

  redirectToActiveSession(router, session);
  return false;
};

export const hasSavedFormStateGuard: CanActivateFn = () => {
  const router = inject(Router);
  const session = inject(SessionService);
  const captureService = inject(CaptureService);

  if (captureService.getSavedFormState()) {
    return true;
  }

  redirectToActiveSession(router, session);
  return false;
};

export const hasActiveSessionGuard: CanActivateFn = () => {
  const router = inject(Router);
  const session = inject(SessionService);

  if (session.currentSession()) {
    return true;
  }

  redirectToActiveSession(router, session);
  return false;
};
